// streamingFetch.js
// Cross-platform NDJSON streaming reader for AI responses.
//
// Reads a POST stream from the backend that emits one JSON object per line.
// Calls onEvent(parsedObject) for each line as it arrives.
//
// Works on:
//   - Web (Chrome/Safari/Edge): native ReadableStream + TextDecoder
//   - Electron: native ReadableStream + TextDecoder
//   - React Native >= 0.72: native ReadableStream (Hermes)
//   - React Native < 0.72: falls back to non-streaming response.text()

import axios from "axios";

/**
 * Stream NDJSON events from a POST endpoint.
 *
 * @param {Object} opts
 * @param {string} opts.url - Full URL to POST to
 * @param {Object} opts.body - JSON body to send
 * @param {Object} [opts.headers] - Extra headers (e.g. Authorization)
 * @param {AbortSignal} [opts.signal] - Abort signal for cancellation
 * @param {(event: Object) => void} opts.onEvent - Called for each parsed event
 * @returns {Promise<void>} Resolves when stream ends or rejects on error
 */
export async function streamNdjson({ url, body, headers = {}, signal, onEvent }) {
  // Merge axios default Authorization header (set by AuthContext on login)
  const authHeader = axios.defaults.headers.common?.["Authorization"];
  // BDD 04/13/2026 - pass X-Content-DB
  const contentDbHeader = axios.defaults.headers.common?.["X-Content-DB"];

  const mergedHeaders = {
    "Content-Type": "application/json",
    Accept: "application/x-ndjson",
    ...(authHeader ? { Authorization: authHeader } : {}),
    ...(contentDbHeader ? { "X-Content-DB": contentDbHeader } : {}),
    ...headers,
  };

  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: mergedHeaders,
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if (e?.name === "AbortError") return;
    throw new Error(`Network error: ${e?.message || e}`);
  }

  if (!response.ok) {
    let detail = "";
    try {
      detail = await response.text();
    } catch {}
    throw new Error(`HTTP ${response.status}: ${detail || response.statusText}`);
  }

  // Path 1: Native streaming via ReadableStream
  if (response.body && typeof response.body.getReader === "function") {
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    while (true) {
      let chunk;
      try {
        chunk = await reader.read();
      } catch (e) {
        if (e?.name === "AbortError") return;
        throw e;
      }
      const { value, done } = chunk;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      // Last element may be a partial line — keep it in the buffer
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const parsed = JSON.parse(trimmed);
          onEvent(parsed);
        } catch (e) {
          console.warn("[streamNdjson] failed to parse line:", trimmed, e);
        }
      }
    }

    // Flush any remaining buffered content
    const tail = buffer.trim();
    if (tail) {
      try {
        const parsed = JSON.parse(tail);
        onEvent(parsed);
      } catch (e) {
        console.warn("[streamNdjson] failed to parse tail:", tail, e);
      }
    }
    return;
  }

  // Path 2: Fallback for environments without ReadableStream support
  // Read full response text and dispatch events at once (non-progressive but functional)
  console.warn("[streamNdjson] ReadableStream unavailable, using buffered fallback");
  if (signal?.aborted) return;
  let fullText;
  try {
    fullText = await response.text();
  } catch (e) {
    if (e?.name === "AbortError") return;
    throw e;
  }
  if (signal?.aborted) return;
  const lines = fullText.split("\n");
  for (const line of lines) {
    if (signal?.aborted) return;
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      const parsed = JSON.parse(trimmed);
      onEvent(parsed);
    } catch (e) {
      console.warn("[streamNdjson] failed to parse fallback line:", trimmed, e);
    }
  }
}
